import React from 'react';
import { Form, Tooltip, OverlayTrigger } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';

import AutocompletedAddressFormGroup from 'components/form/autocompleted_address_form_group';
import BrokersList from '../brokers_list';
import { formatWithCommas } from 'helpers/formatters/number_formats';

// Optional fields that only render in the accuracy section if AI populated them.
export const ACCURACY_SHOULD_SHOW_GATED = new Set([
  'county', 'name', 'location_description', 'building_size', 'building_class',
  'sale_price', 'lease_rate', 'lot_size', 'number_of_units',
  'zoning', 'year_built', 'year_renovated', 'occupancy',
]);

export const ACCURACY_FIELDS_BEFORE_STATE_ZIP = ['country_id', 'address', 'city'];
export const ACCURACY_FIELDS_BEFORE_AI = [
  'county', 'broker_name', 'property_type_id', 'name', 'sale_or_lease',
];
export const ACCURACY_FIELDS_AFTER_AI = [
  'location_description', 'building_size', 'building_class',
  'sale_price', 'lease_rate', 'lot_size', 'number_of_units',
  'zoning', 'year_built', 'year_renovated', 'occupancy',
];

export function renderActionRequiredIcon(fieldName, actionRequiredFields) {
  if (!actionRequiredFields?.has(fieldName)) return null;
  return (
    <FontAwesomeIcon
      className="me-2"
      icon={faTriangleExclamation}
      style={{ color: '#FFC107' }}
    />
  );
}

export function renderInferredIcon(fieldName, inferredFields) {
  if (!inferredFields.has(fieldName)) return null;
  const tooltip = (
    <Tooltip id={`inferred-${fieldName}`}>
      An exact match wasn&apos;t found in the document, so this value was predicted based on context.
      Please review for accuracy.
    </Tooltip>
  );
  return (
    <OverlayTrigger overlay={tooltip} placement="bottom">
      <span className="ms-2">
        <FontAwesomeIcon icon={faInfoCircle} style={{ color: '#6c757d', cursor: 'help' }} />
      </span>
    </OverlayTrigger>
  );
}

/* eslint-disable camelcase */
export function renderField(fieldName, ctx) {
  const {
    formState,
    countryList, propertyTypes, defaultBrokerId, maxBrokersLimit,
    brokers, setBrokers,
    handleChange, handleCountryChange,
    validationTriggered,
    isAddressInvalid, isCityInvalid, isCountryInvalid, isStateInvalid,
    isZipInvalid, isPropertyTypeInvalid, isBrokersInvalid, isSaleOrLeaseInvalid,
    inferredFields,
    unresolvedRequiredFields,
  } = ctx;
  const {
    address, city, state, zip, county, country,
    sale_or_lease, name, location_description, property_type_id,
    building_size, building_class, sale_price, lease_rate,
    lot_size, number_of_units, zoning, year_built, year_renovated, occupancy,
  } = formState;
  const inferredIcon = n => renderInferredIcon(n, inferredFields);
  const actionRequiredIcon = n => renderActionRequiredIcon(n, unresolvedRequiredFields);

  switch (fieldName) {
  case 'country_id':
    if (countryList.length <= 1) return null;
    return (
      <Form.Group className="form-group required" key="country_id">
        <Form.Label>
          {actionRequiredIcon('country_id')}
          Country
          {inferredIcon('country_id')}
        </Form.Label>
        <Form.Select
          className="form-control"
          isInvalid={isCountryInvalid && validationTriggered}
          name="country"
          required
          value={country?.id || ''}
          onChange={(e) => {
            const selected = countryList.find(c => c.id === Number(e.target.value));
            if (selected) handleCountryChange(selected);
          }}
        >
          {countryList.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </Form.Select>
        {isCountryInvalid && validationTriggered && (
          <Form.Control.Feedback type="invalid">This field is required</Form.Control.Feedback>
        )}
      </Form.Group>
    );

  case 'address':
    return (
      <AutocompletedAddressFormGroup
        address={address}
        city={city}
        country={country}
        feedback={isAddressInvalid && validationTriggered && 'This field is required'}
        feedbackType="invalid"
        hasError={isAddressInvalid && validationTriggered}
        key="address"
        label={
          <Form.Label>
            {actionRequiredIcon('address')}
            Address
            {inferredIcon('address')}
          </Form.Label>
        }
        required
        state={state}
        zip={zip}
        onChange={handleChange}
      />
    );

  case 'city':
    return (
      <Form.Group className="form-group required" key="city">
        <Form.Label>
          {actionRequiredIcon('city')}
          City
          {inferredIcon('city')}
        </Form.Label>
        <Form.Control
          isInvalid={isCityInvalid && validationTriggered}
          name="city"
          required
          type="text"
          value={city}
          onChange={e => handleChange({ city: e.target.value })}
        />
        {isCityInvalid && validationTriggered && (
          <Form.Control.Feedback type="invalid">This field is required</Form.Control.Feedback>
        )}
      </Form.Group>
    );

  case 'state':
    if (!country?.countryStates?.length) return null;
    return (
      <Form.Group className="form-group required" key="state">
        <Form.Label>
          {actionRequiredIcon('state')}
          {country.stateLabel}
          {inferredIcon('state')}
        </Form.Label>
        <Form.Select
          className="form-control"
          isInvalid={isStateInvalid && validationTriggered}
          name="state"
          required
          value={state}
          onChange={e => handleChange({ state: e.target.value })}
        >
          <option value="">Select a state</option>
          {country.countryStates.map(s => (
            <option key={s.code} value={s.code}>{s.displayName}</option>
          ))}
        </Form.Select>
        {isStateInvalid && validationTriggered && (
          <Form.Control.Feedback type="invalid">This field is required</Form.Control.Feedback>
        )}
      </Form.Group>
    );

  case 'zip':
    if (!country?.zipLabel) return null;
    return (
      <Form.Group className="form-group required" key="zip">
        <Form.Label>
          {actionRequiredIcon('zip')}
          {country.zipLabel}
          {inferredIcon('zip')}
        </Form.Label>
        <Form.Control
          isInvalid={isZipInvalid && validationTriggered}
          name="zip"
          required
          type="text"
          value={zip}
          onChange={e => handleChange({ zip: e.target.value })}
        />
        {isZipInvalid && validationTriggered && (
          <Form.Control.Feedback type="invalid">This field is required</Form.Control.Feedback>
        )}
      </Form.Group>
    );

  case 'county':
    if (!country?.hasCounty) return null;
    return (
      <Form.Group className="form-group" key="county">
        <Form.Label>
          County
          {inferredIcon('county')}
        </Form.Label>
        <Form.Control
          name="county"
          type="text"
          value={county || ''}
          onChange={e => handleChange({ county: e.target.value })}
        />
      </Form.Group>
    );

  case 'property_type_id':
    return (
      <Form.Group className="form-group required" key="property_type_id">
        <Form.Label>
          {actionRequiredIcon('property_type_id')}
          Primary Property Type
          {inferredIcon('property_type_id')}
        </Form.Label>
        <Form.Select
          className="form-control"
          isInvalid={isPropertyTypeInvalid && validationTriggered}
          name="property_type_id"
          required
          value={property_type_id || ''}
          onChange={e => handleChange({ property_type_id: e.target.value })}
        >
          <option value="">Select a property type</option>
          {propertyTypes && Object.entries(propertyTypes).map(([key, value]) => (
            <option key={key} value={key}>{value}</option>
          ))}
        </Form.Select>
        {isPropertyTypeInvalid && validationTriggered && (
          <Form.Control.Feedback type="invalid">This field is required</Form.Control.Feedback>
        )}
      </Form.Group>
    );

  case 'broker_name':
    if (defaultBrokerId) return null;
    return (
      <Form.Group className="form-group" key="broker_name">
        <BrokersList
          brokers={brokers}
          isInvalid={isBrokersInvalid && validationTriggered}
          maxBrokersLimit={maxBrokersLimit}
          primaryLabelPrefixIcon={actionRequiredIcon('broker_name')}
          required
          onSetBrokers={setBrokers}
        />
        {isBrokersInvalid && validationTriggered && (
          <div className="text-danger small mt-1">This field is required</div>
        )}
      </Form.Group>
    );

  case 'sale_or_lease':
    return (
      <Form.Group className="form-group required" key="sale_or_lease">
        <Form.Label>
          {actionRequiredIcon('sale_or_lease')}
          Sale or Lease
          {inferredIcon('sale_or_lease')}
        </Form.Label>
        <Form.Select
          className="form-control"
          isInvalid={isSaleOrLeaseInvalid && validationTriggered}
          name="sale_or_lease"
          value={sale_or_lease || ''}
          onChange={e => handleChange({ sale_or_lease: e.target.value })}
        >
          <option value="">Select…</option>
          <option value="sale">Sale</option>
          <option value="lease">Lease</option>
        </Form.Select>
        {isSaleOrLeaseInvalid && validationTriggered && (
          <Form.Control.Feedback type="invalid">This field is required</Form.Control.Feedback>
        )}
      </Form.Group>
    );

  case 'name':
    return (
      <Form.Group className="form-group" key="name">
        <Form.Label>
          Property Name
          {inferredIcon('name')}
        </Form.Label>
        <Form.Control
          name="name"
          type="text"
          value={name || ''}
          onChange={e => handleChange({ name: e.target.value })}
        />
      </Form.Group>
    );

  case 'location_description':
    return (
      <Form.Group className="form-group" key="location_description">
        <Form.Label>
          Location Description
          {inferredIcon('location_description')}
        </Form.Label>
        <Form.Control
          as="textarea"
          name="location_description"
          rows={3}
          value={location_description || ''}
          onChange={e => handleChange({ location_description: e.target.value })}
        />
      </Form.Group>
    );

  case 'building_size':
    return (
      <Form.Group className="form-group" key="building_size">
        <Form.Label>
          Building Size (SF)
          {inferredIcon('building_size')}
        </Form.Label>
        <Form.Control
          name="building_size"
          type="text"
          value={building_size || ''}
          onChange={e => handleChange({ building_size: e.target.value })}
        />
      </Form.Group>
    );

  case 'building_class':
    return (
      <Form.Group className="form-group" key="building_class">
        <Form.Label>
          Building Class
          {inferredIcon('building_class')}
        </Form.Label>
        <Form.Select
          className="form-control"
          name="building_class"
          value={building_class || ''}
          onChange={e => handleChange({ building_class: e.target.value })}
        >
          <option value="">Select a building class</option>
          <option value="A">A</option>
          <option value="B">B</option>
          <option value="C">C</option>
        </Form.Select>
      </Form.Group>
    );

  case 'sale_price':
    return (
      <Form.Group className="form-group" key="sale_price">
        <Form.Label>
          Sale Price
          {inferredIcon('sale_price')}
        </Form.Label>
        <Form.Control
          name="sale_price"
          type="text"
          value={sale_price ? formatWithCommas(sale_price) : ''}
          onChange={e => handleChange({ sale_price: e.target.value.replace(/[^0-9.]/g, '') })}
        />
      </Form.Group>
    );

  case 'lease_rate':
    return (
      <Form.Group className="form-group" key="lease_rate">
        <Form.Label>
          Lease Rate
          {inferredIcon('lease_rate')}
        </Form.Label>
        <Form.Control
          name="lease_rate"
          type="text"
          value={lease_rate ? formatWithCommas(lease_rate) : ''}
          onChange={e => handleChange({ lease_rate: e.target.value.replace(/[^0-9.]/g, '') })}
        />
      </Form.Group>
    );

  case 'lot_size':
    return (
      <Form.Group className="form-group" key="lot_size">
        <Form.Label>
          Lot Size (Acres)
          {inferredIcon('lot_size')}
        </Form.Label>
        <Form.Control
          name="lot_size"
          type="number"
          value={lot_size || ''}
          onChange={e => handleChange({ lot_size: e.target.value })}
        />
      </Form.Group>
    );

  case 'number_of_units':
    return (
      <Form.Group className="form-group" key="number_of_units">
        <Form.Label>
          Number of Units
          {inferredIcon('number_of_units')}
        </Form.Label>
        <Form.Control
          name="number_of_units"
          type="number"
          value={number_of_units || ''}
          onChange={e => handleChange({ number_of_units: e.target.value })}
        />
      </Form.Group>
    );

  case 'zoning':
    return (
      <Form.Group className="form-group" key="zoning">
        <Form.Label>
          Zoning
          {inferredIcon('zoning')}
        </Form.Label>
        <Form.Control
          name="zoning"
          type="text"
          value={zoning || ''}
          onChange={e => handleChange({ zoning: e.target.value })}
        />
      </Form.Group>
    );

  case 'year_built':
    return (
      <Form.Group className="form-group" key="year_built">
        <Form.Label>
          Year Built
          {inferredIcon('year_built')}
        </Form.Label>
        <Form.Control
          name="year_built"
          type="number"
          value={year_built || ''}
          onChange={e => handleChange({ year_built: e.target.value })}
        />
      </Form.Group>
    );

  case 'year_renovated':
    return (
      <Form.Group className="form-group" key="year_renovated">
        <Form.Label>
          Year Renovated
          {inferredIcon('year_renovated')}
        </Form.Label>
        <Form.Control
          name="year_renovated"
          type="number"
          value={year_renovated || ''}
          onChange={e => handleChange({ year_renovated: e.target.value })}
        />
      </Form.Group>
    );

  case 'occupancy':
    return (
      <Form.Group className="form-group" key="occupancy">
        <Form.Label>
          Occupancy
          {inferredIcon('occupancy')}
        </Form.Label>
        <Form.Control
          name="occupancy"
          type="number"
          value={occupancy || ''}
          onChange={e => handleChange({ occupancy: e.target.value })}
        />
      </Form.Group>
    );

  default:
    return null;
  }
}
/* eslint-enable camelcase */
